import { DisplayFormat, formatTicker } from '@api';
import {
  CustomMixDomain,
  hasOHLC,
  RangeHorizon,
  ResampleIntervals,
  SeriesType,
} from '@chart';
import {
  ChartV2Handle,
  ChartV2Horizon,
  ChartV2Interval,
  ChartV2Point,
  ChartV2PriceDisplay,
  ChartV2Series,
  ChartV2SeriesType,
} from '@chartv2';
import { isPriceSnake, isYieldSnake } from '@helpers';
import { SeriesLegendLabel } from '@terminal/views/chart-v2/components/SeriesLegendLabel';
import { SeriesLegendStatus } from '@terminal/views/chart-v2/components/SeriesLegendStatus';
import type { ChartV2SyntheticBars } from '@terminal/views/chart-v2/hooks/useChartV2LiveTicks';
import type {
  TerminalChartAssetData,
  TerminalChartEntity,
  TerminalPaneData,
} from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { createElement } from 'react';

export interface TerminalPanesToSeriesOptions {
  /** Called when the user clicks the ticker/logo — typically opens the "change data" search modal. */
  onChangeTicker?: (entity: TerminalChartEntity, paneIndex: number) => void;
  /** Called when the user clicks the indicator name — typically opens the indicator picker. */
  onChangeIndicator?: (entity: TerminalChartEntity) => void;
  /** Tooltip label for the ticker button (e.g. `t('chart:changeData')`). */
  changeDataLabel?: string;
  /** Tooltip label for the indicator button (e.g. `t('chart:changeIndicator')`). */
  changeIndicatorLabel?: string;
  /** Synthetic live bars keyed by `subscribable_ticker`, produced by {@link useChartV2LiveTicks}. */
  liveTicks?: ChartV2SyntheticBars;
}

const isPriceOrYieldSnake = (snake: string | undefined): boolean =>
  !!snake && (isPriceSnake(snake) || isYieldSnake(snake));

/**
 * Append the accumulated synthetic bars (one per minute of elapsed live
 * ticks) to the tail of an asset's `ts`. All bar accumulation logic —
 * rolling over to a new bar every interval, extending high/low, resetting
 * when historical data catches up — lives in {@link useChartV2LiveTicks}.
 * We just splice the result in here so both the chart line and the
 * legend's `ts[-1].close` read see the same data.
 */
const applyLiveTick = (
  asset: TerminalChartAssetData,
  liveTicks: ChartV2SyntheticBars | undefined
): TerminalChartAssetData => {
  if (!isPriceOrYieldSnake(asset.snakeMeta?.snake)) {
    return asset;
  }

  const subscribable = asset.entity.subscribable_ticker;
  const bars = subscribable ? liveTicks?.[subscribable] : undefined;

  if (!bars || bars.length === 0) {
    return asset;
  }

  return {
    ...asset,
    ts: [...asset.ts, ...bars],
  };
};

const RANGE_TO_CHARTV2_HORIZON = {
  [RangeHorizon.OneDay]: '1D',
  [RangeHorizon.OneWeek]: '1W',
  [RangeHorizon.TwoWeeks]: '2W',
  [RangeHorizon.OneMonth]: '1M',
  [RangeHorizon.TwoMonths]: '2M',
  [RangeHorizon.ThreeMonths]: '3M',
  [RangeHorizon.SixMonths]: '6M',
  [RangeHorizon.OneYear]: '1Y',
  [RangeHorizon.ThreeYears]: '3Y',
  [RangeHorizon.FiveYears]: '5Y',
  [RangeHorizon.TenYears]: '10Y',
  [RangeHorizon.Max]: 'MAX',
} satisfies Record<RangeHorizon, ChartV2Horizon>;

const CHARTV2_HORIZON_TO_RANGE = Object.fromEntries(
  Object.entries(RANGE_TO_CHARTV2_HORIZON).map(
    ([rangeHorizon, chartV2Horizon]) => [chartV2Horizon, rangeHorizon]
  )
) as Record<ChartV2Horizon, RangeHorizon>;

export const toChartV2Horizon = (
  horizon: RangeHorizon | null | undefined
): ChartV2Horizon | undefined =>
  horizon ? RANGE_TO_CHARTV2_HORIZON[horizon] : undefined;

export const toRangeHorizon = (horizon: ChartV2Horizon): RangeHorizon =>
  CHARTV2_HORIZON_TO_RANGE[horizon];

const RESAMPLE_TO_CHARTV2_INTERVAL: Record<ResampleIntervals, ChartV2Interval> =
  {
    [ResampleIntervals.OneMinute]: '1m',
    [ResampleIntervals.FiveMinutes]: '5m',
    [ResampleIntervals.FifteenMinutes]: '15m',
    [ResampleIntervals.ThirtyMinutes]: '30m',
    [ResampleIntervals.OneHour]: '1h',
    [ResampleIntervals.OneDay]: '1D',
    [ResampleIntervals.OneWeek]: '1W',
    [ResampleIntervals.OneMonth]: '1M',
  };

const CHARTV2_INTERVAL_TO_RESAMPLE: Record<ChartV2Interval, ResampleIntervals> =
  {
    '1m': ResampleIntervals.OneMinute,
    '5m': ResampleIntervals.FiveMinutes,
    '15m': ResampleIntervals.FifteenMinutes,
    '30m': ResampleIntervals.ThirtyMinutes,
    '1h': ResampleIntervals.OneHour,
    '1D': ResampleIntervals.OneDay,
    '1W': ResampleIntervals.OneWeek,
    '1M': ResampleIntervals.OneMonth,
  };

export const toChartV2Interval = (
  resample: ResampleIntervals
): ChartV2Interval => RESAMPLE_TO_CHARTV2_INTERVAL[resample];

export const toResampleInterval = (
  interval: ChartV2Interval
): ResampleIntervals => CHARTV2_INTERVAL_TO_RESAMPLE[interval];

const SERIES_TYPE_TO_CHARTV2: Record<SeriesType, ChartV2SeriesType> = {
  line: 'line',
  bars: 'bar',
  candlestick: 'candlestick',
};

const toChartV2SeriesType = (
  type: SeriesType | undefined
): ChartV2SeriesType =>
  type ? (SERIES_TYPE_TO_CHARTV2[type] ?? 'line') : 'line';

export const getChartV2SeriesId = (asset: TerminalChartAssetData): string =>
  `${asset.entity.tag}:${asset.snakeMeta?.snake ?? 'default'}`;

const ISO_DOMAIN_STRING_WITH_TIMEZONE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const parseTsTime = (time: string): number => {
  const ms = Date.parse(time);

  return Number.isNaN(ms) ? 0 : ms;
};

const resolveDomainValue = (
  value: CustomMixDomain[number],
  primaryAsset: TerminalChartAssetData
): number | null => {
  if (typeof value === 'number') {
    const point = primaryAsset.ts[Math.trunc(value)];
    const time = point ? Date.parse(point.time) : Number.NaN;

    return Number.isNaN(time) ? null : time;
  }

  if (!ISO_DOMAIN_STRING_WITH_TIMEZONE.test(value)) {
    return null;
  }

  const time = Date.parse(value);

  return Number.isNaN(time) ? null : time;
};

export const toChartV2Domain = (
  domain: CustomMixDomain | null | undefined,
  primaryAsset: TerminalChartAssetData | undefined
): Parameters<ChartV2Handle['setDomain']>[0] | null => {
  if (!domain || !primaryAsset) {
    return null;
  }

  const start = resolveDomainValue(domain[0], primaryAsset);
  const end = resolveDomainValue(domain[1], primaryAsset);

  if (start === null || end === null || start >= end) {
    return null;
  }

  return { start, end };
};

/**
 * The terminal's time-series items always have open/high/low/close fields,
 * but for snake-derived values (non-OHLC feeds) `createTsDataFromSnake`
 * populates open/high/low with placeholder zeros. If we emit those as OHLC
 * points chartv2's y-extent ends up driven by the zero high/low rather than
 * the real close, so we fall back to a line point whenever the OHLC shape
 * is clearly fake.
 */
const hasRealOhlc = (item: TerminalChartAssetData['ts'][number]): boolean =>
  !(item.open === 0 && item.high === 0 && item.low === 0);

const toChartV2Point = (
  item: TerminalChartAssetData['ts'][number]
): ChartV2Point =>
  hasRealOhlc(item)
    ? {
        time: parseTsTime(item.time),
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
      }
    : {
        time: parseTsTime(item.time),
        value: item.close,
      };

/**
 * Y-axis column header. Mirrors the original chart's `AxisCurrency`:
 *   • `%` when the snake is a percentage series.
 *   • Uppercase currency ISO code (e.g. `"USD"`, `"GBP"`) for
 *     currency-denominated series.
 *   • Empty for everything else — the original filters ratio /
 *     count / boolean indicators out of the y-axis label entirely
 *     (a long indicator name like "Net Debt / EBITDA" overflows the
 *     pill and clips into adjacent columns).
 */
const getYAxisUnit = (asset: TerminalChartAssetData): string => {
  const { snakeMeta, entity } = asset;
  const displayFormat = snakeMeta?.display_format;

  if (displayFormat === DisplayFormat.PERCENT) {
    return '%';
  }

  const currency = (snakeMeta?.currency || entity.currency || '').toUpperCase();

  if (displayFormat === DisplayFormat.CURRENCY && currency) {
    return currency;
  }

  return '';
};

/**
 * The original chart gates the market-hours icons (WavePulse / CalendarClock
 * / SunRise / etc) behind `isPrimary={hasOHLC(primaryAsset)}` — the flag is
 * applied to every legend row, not just the first one. Mirroring that here
 * means every asset in an OHLC-driven chart (stock prices, etc) gets its
 * market-status icon, regardless of pane position.
 */
const assetToSeries = (
  rawAsset: TerminalChartAssetData,
  paneIndex: number,
  paneSeriesType: SeriesType | undefined,
  isPrimary: boolean,
  options: TerminalPanesToSeriesOptions
): ChartV2Series => {
  const asset = applyLiveTick(rawAsset, options.liveTicks);
  const entity = asset.entity;
  const label = entity.formattedTicker || formatTicker(entity) || entity.name;
  const data = asset.ts.map(toChartV2Point);
  // Use the turbo-chart palette token the terminal already assigned to
  // this asset so the legend's price text reads in the same hue as the
  // chart line. Close enough to chartv2's internal palette choice (both
  // pick from the same `--chart-multi-lines-hex-*` tokens).
  const legendColor = asset.lineColorToken
    ? `var(${asset.lineColorToken})`
    : 'var(--text-default)';
  // Only enable the horizontal "latest price" reference line on price /
  // yield series — for ratios, recommendations, indicators, etc. the
  // current value isn't meaningful as a standing reference.
  const hasLivePriceOverlay = isPriceOrYieldSnake(asset.snakeMeta?.snake);

  return {
    id: getChartV2SeriesId(asset),
    label,
    // Always emit an explicit string (even empty) so the canvas doesn't
    // fall back to the ticker label for ratios/indicators.
    yAxisLabel: getYAxisUnit(asset),
    showLatestPrice: hasLivePriceOverlay,
    legendLabel: createElement(SeriesLegendLabel, {
      entity,
      snakeMeta: asset.snakeMeta,
      changeDataLabel: options.changeDataLabel,
      changeIndicatorLabel: options.changeIndicatorLabel,
      onTickerClick: options.onChangeTicker
        ? () => options.onChangeTicker?.(entity, paneIndex)
        : undefined,
      onIndicatorClick: options.onChangeIndicator
        ? () => options.onChangeIndicator?.(entity)
        : undefined,
    }),
    legendStatus: createElement(SeriesLegendStatus, {
      asset,
      isPrimary,
      legendColor,
    }),
    seriesType: toChartV2SeriesType(asset.seriesType ?? paneSeriesType),
    data,
    paneId: `pane-${paneIndex}`,
  };
};

export const terminalPanesToSeries = (
  panes: TerminalPaneData[],
  options: TerminalPanesToSeriesOptions = {}
): ChartV2Series[] => {
  const primaryAsset = panes[0]?.chartAssetData[0];
  const isOhlcChart = hasOHLC(primaryAsset);

  return panes.flatMap((pane, paneIndex) =>
    pane.chartAssetData.map(asset =>
      assetToSeries(asset, paneIndex, pane.seriesType, isOhlcChart, options)
    )
  );
};

export const terminalPanesToDefaultHiddenSeriesIds = (
  panes: TerminalPaneData[]
): string[] =>
  panes.flatMap(pane =>
    pane.chartAssetData
      .filter(asset => asset.isHidden)
      .map(asset => getChartV2SeriesId(asset))
  );

export const terminalPanesToDefaultPriceDisplayByPane = (
  panes: TerminalPaneData[]
): Record<string, ChartV2PriceDisplay> =>
  panes.reduce<Record<string, ChartV2PriceDisplay>>((result, pane, index) => {
    if (pane.yAxisType === 'merged') {
      result[`pane-${index}`] = 'indexed';
    }

    return result;
  }, {});
